import React, {
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
  useContext
} from 'react';
import router, { useRouter } from 'next/router';

import { useQuery } from '@tanstack/react-query';

import Loader from '@atoms/Atom/Loader';

import LinkLibraryItem from '@molecules/LinkLibraryItem';
import useOutsideClickDetect from '@lib/hooks/useOutsideClickDetect';
import { getCorrectedFilter, processDeletedLinks } from '@lib/link-library-helper';

import { StreamDataContext } from '@components/context/StreamContext';

import { getAuth } from '@services/identity.service';
import { deleteLinkLibrary } from '@services/link-library.service';
import { updateEngageType, getLinkLibraryPaginated } from '@services/youtube-platform.service';

import ShareModel from './ShareModel';
import { ActionType } from './LinkLibraryReducer';
import LinkLibraryDropdown from './LinkLibraryDropdown';

import useNotificationTimer from '@lib/hooks/useNotificationTimer ';
import useFonts from '@lib/hooks/useFonts';
import useFontSizeCalculator from '@components/Presentation/helpers/fontSizeCalculator';

const LinkLibrary = forwardRef(({
  streamName,
  streamId,
  isResult,
  showUserName,
  linksData,
  platformType,
  pushCmdToFS,
  currentRosFeature,
  setShowNotification,
  isSandbox = false,
  isScreenshot,
  livestreamData
}, ref) => {
  const joinType = useRouter().query?.joinType;
  const [refetch, setRefetch] = useState(true);
  const [linkData, setLinkData] = useState([]);
  const [atBottom, setAtBottom] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showShareModel, setShowShareModel] = useState(false);
  const [currentFilter, setCurrentFilter] = useState(currentRosFeature?.setting?.LinkType || 'All');

  const auth = getAuth();

  const scrollContainerRef = useRef(null);

  useOutsideClickDetect(showDropdown, 'LinkTypeDropDown', setShowDropdown);
  useNotificationTimer(currentRosFeature, linkData, setShowNotification);

  const stopCurrentFeature = () => {
    router.push({ pathname: `/streams/${streamId}`, query: joinType && { joinType } });
  };

  useImperativeHandle(ref, () => ({ stopCurrentFeature }));

  const {
    linkLibraryState: { links, filters, allLinks, groupedLinks },
    dispatchLinkLibrary: dispatch
  } = useContext(StreamDataContext);

  const setLinks = (newLinks) => {
    dispatch({
      type: ActionType.SET_LINKS,
      links: newLinks,
    });
  };

  const setAllLinks = (newLinks) => {
    dispatch({
      type: ActionType.SET_ALL_LINKS,
      links: newLinks,
    });
  };

  const groupDeletedLinks = (links) => {
    dispatch({
      type: ActionType.UPDATE_GROUPED_DELETED_LINKS,
      links
    });
  };

  const handleSetLinks = (newLinks) => {
    setLinks(newLinks);
    if (newLinks.length === 10 &&
      currentFilter?.toLowerCase() === 'all') {
      setAllLinks([...allLinks, ...newLinks]);
    }
    setLinkData([...allLinks, ...newLinks]);
  };

  const handleScroll = () => {
    const { scrollTop, clientHeight, scrollHeight } = scrollContainerRef.current;
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 20;

    setAtBottom(isAtBottom);
    setRefetch(isAtBottom);
  };

  const handleDelete = async (index, syncToFs = false) => {
    if (index === undefined || index === null) return;
    const link = linkData[index];
    dispatch({
      type: ActionType.DELETE_LINK,
      link
    });
    const response = await deleteLinkLibrary(link.id);
    groupDeletedLinks(link);

    if (!response.status && !response.data?.entity) {
      setLinks([...links].splice(index, 0, link));
      dispatch({
        type: ActionType.ADD_LINK,
        link,
        index
      });
    } else if (syncToFs) {
      pushCmdToFS('deleteLinkLibraryLink', { link });
    }
  };

  const handleSelectedFilter = (newFilters, syncToFS = false) => {
    setShowDropdown(false);
    setCurrentFilter(getCorrectedFilter(newFilters.linkType));

    if (syncToFS) {
      dispatch({
        type: ActionType.UPDATE_FILTERS,
        filters: newFilters
      });
      if (pushCmdToFS) { pushCmdToFS('setLinkLibraryFilters', { filters: newFilters }); }
    }
  };

  const { data, isLoading, isFetching } = useQuery(
    {
      queryKey: ['paginated_links', currentFilter],
      queryFn: () => getLinkLibraryPaginated(streamId, {
        skip: filters.linkType?.toLowerCase()
          === 'all' ? allLinks.length || 0 : 0,
        filter: currentFilter
      }, auth),
      enabled: !isScreenshot,
      keepPreviousData: false,
      refetchInterval: refetch || atBottom ? 3000 : false,
    },

  );

  useEffect(() => {
    dispatch({
      type: 'UPDATE_FILTERS',
      filters: {
        linkType: currentRosFeature?.setting?.LinkType || 'All'
      }
    });
  }, [currentRosFeature]);

  useEffect(() => {
    if (data?.entity?.rows) {
      const filteredLinks = processDeletedLinks(data?.entity?.rows, groupedLinks, deleteLinkLibrary);
      handleSetLinks(filteredLinks);
    }
    if (data?.entity?.count === 0) {
      dispatch({ type: ActionType.RESET_STATES });
    }
  }, [data]);

  useEffect(() => {
    if (linksData?.length > 0) return;
    updateEngageType(streamId, 'linkLibrary', { platformType });
    const scrollContainer = scrollContainerRef.current;
    scrollContainer?.addEventListener('scroll', handleScroll);
    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    const filter = getCorrectedFilter(filters.linkType);
    setCurrentFilter(prev =>
      prev !== filter ? filter : prev);
    if (filter?.toLowerCase() !== 'all') setLinkData(links);
    const { scrollHeight, clientHeight, scrollTop } = scrollContainerRef.current;
    if (scrollHeight <= clientHeight + scrollTop) setRefetch(true);
    else setRefetch(false);
  }, [links, allLinks, filters]);

  useEffect(() => {
    if (isResult && linksData?.length > 0) {
      setLinkData(linksData);
      return;
    }
    if (linkData.length > 0 && !isResult) {
      setTimeout(() => {
        setShowNotification(false);
      }, 500);
    }
  }, [currentRosFeature, linkData]);

  const bgImg = livestreamData?.settings?.linkLibraryImageSrc;
  let bgColor;
  let textColor = currentRosFeature?.color?.linkLibraryTextColor || '#ffffff';
  const { getFontStyle } = useFonts();
  const fontFamily = getFontStyle(currentRosFeature?.setting?.FontStyle || 'Sans-Serif');
  const computedFontSize = useFontSizeCalculator(currentRosFeature?.setting?.linkLibraryFontSize ? Number(currentRosFeature?.setting?.linkLibraryFontSize) : 1, 1.8, 'rem');
  if (isResult) {
    bgColor = currentRosFeature?.color?.linkLibraryBgColor || '#111111';
  }

  return (
    <div
      style={{
        zIndex: 1,
        color: textColor,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundColor: bgImg ? 'transparent' : bgColor,
        backgroundImage: isResult && bgImg ? `url(${process.env.NEXT_PUBLIC_BUCKET_URL}${bgImg})` : 'none',
      }}
      className={`h-full pb-16 ${isResult && 'px-4! pt-4 pb-0! aspect-video rounded-[16px] overflow-hidden flex flex-col bg-linear-to-br from-grey-400 to-grey-100'}`}>

      {showShareModel && <ShareModel
        selectedFilter={filters.linkType}
        setShowShareModel={setShowShareModel}
        streamId={streamId}
        isResult={isResult}
        streamName={streamName} />}
      <div className='pt-2 px-1 z-10 relative'>
        <LinkLibraryDropdown
          filters={filters}
          showDropdown={showDropdown}
          setShowDropdown={setShowDropdown}
          setShowShareModel={setShowShareModel}
          handleSelectedFilter={handleSelectedFilter}
          showShareButton={!isSandbox && !isResult}
        />
      </div>
      <div
        ref={scrollContainerRef}
        id={`${isResult ? '' : 'scrollableDiv11'}`}
        style={isSandbox ? {
          height: 'calc(100vh - 450px)',
        } : {}}
        className={`z-2 h-full scrollable-container w-full overflow-auto rounded-lg pb-4  ${isResult && linksData ? 'pb-0' : ''}`}
      >

        {isLoading ? <Loader showMessages={false} />
          : (<>  {linkData.length !== 0 ? linkData.map((link, index) => (
            <>
              {!groupedLinks[link.body] &&
                <LinkLibraryItem
                  key={index}
                  id={link.id}
                  index={index}
                  question={link}
                  isResult={isResult}
                  interactionType={'linkLibrary'}
                  isLoading={isLoading}
                  showUserName={showUserName}
                  selectedFilter={filters.linkType}
                  currentRosFeature={currentRosFeature}
                  handleDelete={handleDelete}
                  fontFamily={fontFamily}
                  fontSize={computedFontSize}
                />
              }
            </>
          ))
            : <div className="absolute m-0 top-1/2 left-1/2 transform -translate-y-1/2 -translate-x-1/2">
              <div className='flex flex-col items-center'>
                <svg width="57" height="57" viewBox="0 0 57 57" xmlns="http://www.w3.org/2000/svg" style={{ fill: (textColor || '#fff') }}>
                  <path
                    d="M38.8545 7.78711C34.3461 7.78711 30.4807 11.7596 28.3545 17.8168C26.2282 11.7596 22.3629 7.78711 17.8545 7.78711C10.9857 7.78711 5.60449 17.0118 5.60449 28.7871C5.60449 40.5624 10.9857 49.7871 17.8545 49.7871C22.3629 49.7871 26.2282 45.8146 28.3545 39.7574C30.4807 45.8146 34.3461 49.7871 38.8545 49.7871C45.7232 49.7871 51.1045 40.5624 51.1045 28.7871C51.1045 17.0118 45.7232 7.78711 38.8545 7.78711ZM23.7432 41.5599C22.1048 44.5634 19.9589 46.2871 17.8545 46.2871C15.7501 46.2871 13.6042 44.5634 11.9657 41.5599C10.9065 39.5513 10.1516 37.3964 9.72574 35.1659C10.7919 35.6477 11.9618 35.8547 13.1286 35.7678C14.2953 35.6809 15.4217 35.3029 16.4047 34.6685C17.3877 34.034 18.196 33.1633 18.7557 32.1358C19.3155 31.1084 19.6087 29.9571 19.6087 28.7871C19.6087 27.6171 19.3155 26.4658 18.7557 25.4384C18.196 24.411 17.3877 23.5402 16.4047 22.9057C15.4217 22.2713 14.2953 21.8933 13.1286 21.8064C11.9618 21.7196 10.7919 21.9265 9.72574 22.4084C10.1516 20.1778 10.9065 18.023 11.9657 16.0143C13.6042 13.0109 15.7501 11.2871 17.8545 11.2871C19.9589 11.2871 22.1048 13.0109 23.7432 16.0143C25.5873 19.3962 26.6045 23.933 26.6045 28.7871C26.6045 33.6412 25.5873 38.178 23.7432 41.5599ZM9.10449 28.7871C9.10449 28.0949 9.30976 27.4182 9.69435 26.8426C10.0789 26.267 10.6256 25.8184 11.2651 25.5535C11.9046 25.2886 12.6084 25.2193 13.2873 25.3544C13.9662 25.4894 14.5899 25.8228 15.0794 26.3122C15.5688 26.8017 15.9022 27.4254 16.0372 28.1043C16.1723 28.7832 16.103 29.487 15.8381 30.1265C15.5732 30.766 15.1246 31.3127 14.549 31.6973C13.9734 32.0818 13.2967 32.2871 12.6045 32.2871C11.6762 32.2871 10.786 31.9184 10.1296 31.262C9.47324 30.6056 9.10449 29.7154 9.10449 28.7871ZM44.7432 41.5599C43.1048 44.5634 40.9589 46.2871 38.8545 46.2871C36.7501 46.2871 34.6042 44.5634 32.9657 41.5599C31.9065 39.5513 31.1516 37.3964 30.7257 35.1659C31.7919 35.6477 32.9618 35.8547 34.1286 35.7678C35.2953 35.6809 36.4217 35.3029 37.4047 34.6685C38.3877 34.034 39.196 33.1633 39.7557 32.1358C40.3155 31.1084 40.6087 29.9571 40.6087 28.7871C40.6087 27.6171 40.3155 26.4658 39.7557 25.4384C39.196 24.411 38.3877 23.5402 37.4047 22.9057C36.4217 22.2713 35.2953 21.8933 34.1286 21.8064C32.9618 21.7196 31.7919 21.9265 30.7257 22.4084C31.1516 20.1778 31.9065 18.023 32.9657 16.0143C34.6042 13.0109 36.7501 11.2871 38.8545 11.2871C40.9589 11.2871 43.1048 13.0109 44.7432 16.0143C46.5873 19.3962 47.6045 23.933 47.6045 28.7871C47.6045 33.6412 46.5873 38.178 44.7432 41.5599ZM30.1045 28.7871C30.1045 28.0949 30.3098 27.4182 30.6943 26.8426C31.0789 26.267 31.6256 25.8184 32.2651 25.5535C32.9046 25.2886 33.6084 25.2193 34.2873 25.3544C34.9662 25.4894 35.5899 25.8228 36.0794 26.3122C36.5688 26.8017 36.9022 27.4254 37.0372 28.1043C37.1723 28.7832 37.103 29.487 36.8381 30.1265C36.5732 30.766 36.1246 31.3127 35.549 31.6973C34.9734 32.0818 34.2967 32.2871 33.6045 32.2871C32.6762 32.2871 31.786 31.9184 31.1296 31.262C30.4732 30.6056 30.1045 29.7154 30.1045 28.7871Z"
                  />
                </svg>

                <div className='mt-4' style={{ color: textColor }}>
                  <div className='flex justify-center items-center mb-4'>
                    <span className='text-center text-xl font-semibold'>No Links yet</span><br />
                  </div>
                  <div className='flex justify-center items-center'>
                    <span className='text-center text-lg'>Type in the chat and your links<br /> will appear here.</span>
                  </div>
                </div>
              </div>
            </div>
          }
          </>)}
        <div className='h-10 absolute bottom-0 left-1/2 transform -translate-x-1/2  '>
          {isFetching && !isLoading &&
            < Loader showMessages={false} />
          }
        </div>
      </div >
    </div >
  );
});

export default LinkLibrary;
